import * as Popover from '@radix-ui/react-popover';
import type { SchemaDisplayMetadata } from '@reach/shared-types/schema-mappings';
import {
  resolveSchemaIconName,
  type SchemaIconPickerName,
} from '@reach/shared-types/schema-icon-picker';
import { Palette, Trash2 } from 'lucide-react';
import { type FC, useId, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import styles from './SchemaIconColorPicker.module.css';
import {
  DEFAULT_SCHEMA_COLORS,
  SCHEMA_ICON_OPTIONS,
  SchemaIcon,
  isValidSchemaColor,
} from '@reach/web-ui/schema-display';

interface SchemaIconColorPickerProps {
  value?: SchemaDisplayMetadata | null;
  onApply: (value: SchemaDisplayMetadata) => void;
  disabled?: boolean;
  /** Pixel size of the trigger glyph. */
  triggerSize?: number;
  triggerLabel?: string;
}

const WHEEL_FALLBACK_COLOR = '#111827';

/**
 * Holds a local draft and commits once on Apply, so one edit is one persistence call —
 * each save is a schema version bump upstream.
 */
export const SchemaIconColorPicker: FC<SchemaIconColorPickerProps> = ({
  value,
  onApply,
  disabled,
  triggerSize = 24,
  triggerLabel = 'Choose schema icon and color',
}) => {
  const [open, setOpen] = useState(false);
  const [draftIconName, setDraftIconName] = useState<SchemaIconPickerName | undefined>();
  const [draftColor, setDraftColor] = useState<string | undefined>();
  const [hexInput, setHexInput] = useState('');
  const [wheelOpen, setWheelOpen] = useState(false);
  const wheelId = useId();

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      // Resolve rather than trust: a name stored through the public API may differ in casing.
      const storedIconName = value?.icon?.value;
      setDraftIconName(
        storedIconName ? (resolveSchemaIconName(storedIconName) ?? undefined) : undefined,
      );
      setDraftColor(value?.color ?? undefined);
      setHexInput(value?.color ?? '');
      setWheelOpen(false);
    }
    setOpen(nextOpen);
  };

  const { icon: _storedIcon, color: _storedColor, ...metadataOutsidePicker } = value ?? {};
  const draft: SchemaDisplayMetadata = {
    ...metadataOutsidePicker,
    ...(draftIconName && { icon: { type: 'lucide' as const, value: draftIconName } }),
    ...(draftColor && { color: draftColor }),
  };

  const hexIsInvalid = hexInput.trim().length > 0 && !isValidSchemaColor(hexInput.trim());

  const commit = (metadata: SchemaDisplayMetadata) => {
    onApply(metadata);
    setOpen(false);
  };

  const applyColor = (color: string) => {
    setDraftColor(color);
    setHexInput(color);
  };

  const handleHexChange = (raw: string) => {
    setHexInput(raw);
    const trimmed = raw.trim();
    if (isValidSchemaColor(trimmed)) setDraftColor(trimmed.toLowerCase());
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={styles.trigger}
          style={value?.color ? { borderColor: value.color } : undefined}
          aria-label={triggerLabel}
          title={triggerLabel}>
          <SchemaIcon displayMetadata={value} size={triggerSize} />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className={styles.content}>
          <div className={styles.scrollArea}>
            <div className={styles.preview}>
              <span
                className={styles.previewSwatch}
                style={draftColor ? { borderColor: draftColor } : undefined}>
                <SchemaIcon displayMetadata={draft} size={26} />
              </span>
              <span className={styles.previewLabel}>Preview</span>
            </div>

            <fieldset className={styles.section}>
              <legend className={styles.groupLabel}>Icon</legend>
              <div className={styles.iconGrid}>
                {SCHEMA_ICON_OPTIONS.map(({ name, Icon, label }) => {
                  const isSelected = draftIconName === name;
                  return (
                    <button
                      key={name}
                      type="button"
                      className={styles.iconCell}
                      aria-pressed={isSelected}
                      aria-label={label}
                      title={label}
                      onClick={() => setDraftIconName(isSelected ? undefined : name)}>
                      <Icon size={18} style={draftColor ? { color: draftColor } : undefined} />
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <fieldset className={styles.section}>
              <legend className={styles.groupLabel}>Color</legend>
              <div className={styles.swatchRow}>
                {DEFAULT_SCHEMA_COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    className={styles.swatch}
                    aria-pressed={draftColor?.toLowerCase() === color.toLowerCase()}
                    aria-label={color}
                    title={color}
                    style={{ backgroundColor: color }}
                    onClick={() => applyColor(color)}
                  />
                ))}
              </div>
              <div className={styles.hexRow}>
                <input
                  type="text"
                  className={styles.hexInput}
                  aria-label="Hex color"
                  aria-invalid={hexIsInvalid}
                  placeholder="#22c55e"
                  value={hexInput}
                  onChange={e => handleHexChange(e.target.value)}
                  onFocus={() => setWheelOpen(true)}
                />
                <button
                  type="button"
                  className={styles.hexWheelButton}
                  aria-label={wheelOpen ? 'Hide the colour wheel' : 'Show the colour wheel'}
                  aria-expanded={wheelOpen}
                  aria-controls={wheelId}
                  onClick={() => setWheelOpen(isOpen => !isOpen)}>
                  <Palette size={14} />
                </button>
              </div>
              {wheelOpen && (
                <div id={wheelId} className={styles.colorWheelWrap}>
                  <HexColorPicker
                    color={draftColor ?? WHEEL_FALLBACK_COLOR}
                    onChange={applyColor}
                    className={styles.colorWheel}
                  />
                </div>
              )}
              {hexIsInvalid && <p className={styles.hexError}>Enter a 6-digit hex, e.g. #22c55e</p>}
            </fieldset>
          </div>

          <div className={styles.footer}>
            <button
              type="button"
              className={styles.removeButton}
              onClick={() => commit(metadataOutsidePicker)}>
              <Trash2 size={14} />
              Remove
            </button>
            <button
              type="button"
              className={styles.applyButton}
              disabled={hexIsInvalid}
              onClick={() => commit(draft)}>
              Apply
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
